/* eslint-disable @typescript-eslint/no-explicit-any */
import { useFormikContext, FormikValues, FormikErrors } from 'formik';
import {
    map, startCase, capitalize,
} from 'lodash';
import { useEffect, useState } from 'react';
import { Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { ProblemDetails, ValidationProblemDetails } from '../../../api/web-api-client';
import { ErrorSummaryProps, FormikErrorsSummaryProps } from './types';
import HashLink from '../../Utilities/hashLink';
import { HttpStatusCode } from '../../../types';

const isValidationProblemDetails = (
    value?: ProblemDetails | ValidationProblemDetails,
)
: value is ValidationProblemDetails => (value as ValidationProblemDetails)?.errors !== undefined;

const handleAlertScroll = () => {
    setTimeout(() => {
        const summaryRef: HTMLElement = document.querySelector('#form-error-summary') as HTMLElement;
        summaryRef?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        summaryRef?.focus();
    }, 100);
};

const renderErrorListItem = (
    key: string,
    text: string,
    disableLinkedError?: boolean,
): JSX.Element => {
    if (disableLinkedError) {
        handleAlertScroll();
        return (
            <li key={key}>
                <span className='text-danger fw-bold'>{text}</span>
            </li>
        );
    }

    return (
        <li key={key}>
            <HashLink
                to={`#${key}`}
                className='text-danger fw-bold'
            >
                {text}
            </HashLink>
        </li>
    );
};

const keyToSentenceCase = (key: string, depth?: number, separator = '.') => {
    const keyParts = key.split(separator);
    let sentenceBuilder = '';
    map(keyParts, (keyPart, i) => {
        if (Number.isNaN(+keyPart)) {
            if ((depth && i < depth) || !depth) {
                sentenceBuilder = sentenceBuilder.concat(`${capitalize(startCase(keyPart))}: `);
            }
        } else {
            sentenceBuilder = sentenceBuilder.concat(`(#${+keyPart + 1}): `);
        }
    });
    return sentenceBuilder;
};

const buildErrorText = (error: string, key: string, suppressErrorSummaryPath?: boolean) => {
    const result = suppressErrorSummaryPath ? `${error}` : `${keyToSentenceCase(key, 1, '.')}${error}`;
    return result;
};

const renderErrors = (errors: FormikErrors<FormikValues>, disableLinkedError?: boolean, suppressErrorSummaryPath?: boolean) => (
    <Alert
        variant='danger'
        role='alert'
        aria-live='assertive'
        id='form-error-summary'
        data-testid='form-error-summary'
        className='d-flex'
        tabIndex={-1}
    >
        <div className='d-flex justify-content-center justify-content-md-start mb-3 mb-md-0'>
            <div className='bgCircle me-3'>
                <i className='icon-warning' aria-hidden='true' />
            </div>
        </div>
        <div>
            <Alert.Heading as='h2' className='h5 fw-normal'>
                The following issue(s) must be corrected before you can continue:
            </Alert.Heading>
            <ul>
                {
                    map(Object.keys(errors), (key) => (
                        renderErrorListItem(key, buildErrorText(errors[key] as string, key, suppressErrorSummaryPath), disableLinkedError)
                        // renderErrorListItem(key, `${keyToSentenceCase(key, 1, '.')}${errors[key]}`, disableLinkedError)
                    ))
                }
            </ul>
        </div>
    </Alert>
);

const renderServerError = (error: string | JSX.Element) => (
    <Alert
        variant='danger'
        role='alert'
        aria-live='assertive'
        id='form-error-summary'
        data-testid='form-error-summary'
        className='d-flex'
        tabIndex={-1}
    >
        <div className='d-flex justify-content-center justify-content-md-start mb-3 mb-md-0'>
            <div className='bgCircle me-3'>
                <i className='icon-warning' aria-hidden='true' />
            </div>
        </div>
        <div>
            <span className='text-danger fw-bold'>
                {error}
            </span>
        </div>
    </Alert>
);

const sanitizeErrorData = (
    errorData: Record<string, any>,
    prefixToAdd: false | string = false,
    resultBuilder: Record<string, any> | null = null,
): Record<string, any> => {
    const result: Record<string, any> = resultBuilder ?? {};

    if (prefixToAdd !== false
    && typeof errorData === 'object'
    && errorData !== null
    && Object.keys(errorData).length === 0) {
        result[prefixToAdd] = Array.isArray(errorData) ? [] : {};
        return result;
    }

    const prefix = prefixToAdd !== false
        ? (`${prefixToAdd}.`)
        : '';

    // eslint-disable-next-line no-restricted-syntax
    for (const i in errorData) {
        if (Object.prototype.hasOwnProperty.call(errorData, i)) {
            if (typeof errorData[i] === 'object' && errorData[i] !== null) {
                sanitizeErrorData(errorData[i], prefix + i, result);
            } else if (errorData[i] !== undefined) {
                result[prefix + i] = errorData[i];
            }
        }
    }
    return result;
};

const formatServerErrorKeys = (
    validationErrors: any,
    prefixToRemove?: string,
) => Object.assign({}, ...(map(validationErrors, (value, key) => {
    let keyBuilder = key;

    if (prefixToRemove) {
        keyBuilder = keyBuilder.replace(prefixToRemove, ''); // strip out server dto prefix
    }

    keyBuilder = keyBuilder.replace(/\[/g, '.').replace(/\]/g, ''); // remove array braces
    keyBuilder = map(
        keyBuilder.split('.'),
        (part) => `${part.charAt(0).toLowerCase()}${part.slice(1)}`, // lowercase first letter of each subkey
    ).join('.');

    return { [`${keyBuilder}`]: value[0] };
})));

const FormikErrorsSummary = ({ disableLinkedError, suppressErrorSummaryPath }: FormikErrorsSummaryProps) => {
    const {
        errors, submitCount, isValidating, isSubmitting,
    } = useFormikContext<FormikValues>();
    const [errorSummary, setErrorSummary] = useState<FormikErrors<FormikValues>>({});
    const errorSummaryKeys = Object.keys(errorSummary);
    useEffect(() => {
        // Add an extra condition to allow the summary to be cleared when navigating backwards
        if (submitCount > 0) {
            setErrorSummary(errors);
        } else if (submitCount === 0
                && errors && Object.keys(errors).length === 0
                && errorSummaryKeys.length > 0) {
            setErrorSummary({});
        }
        if (isSubmitting && !isValidating) {
            handleAlertScroll();
        }
    }, [errors, submitCount, isValidating, isSubmitting, errorSummary]);
    if (!errorSummary || errorSummaryKeys.length === 0) {
        return null;
    }
    return renderErrors(sanitizeErrorData(errorSummary), disableLinkedError, suppressErrorSummaryPath);
};

const ErrorSummary = ({
    serverErrors, suppressErrorSummaryPath, prefixToRemove, disableLinkedError, isWafViolation,
}: ErrorSummaryProps) => {
    useEffect(() => {
        if (serverErrors) {
            handleAlertScroll();
        }
    }, [serverErrors]);
    if (isValidationProblemDetails(serverErrors)) {
        const { errors: validationErrors } = serverErrors;
        if (validationErrors) {
            return renderErrors(
                sanitizeErrorData(
                    formatServerErrorKeys(validationErrors, prefixToRemove),
                ),
                disableLinkedError,
                suppressErrorSummaryPath,
            );
        }
    }

    // 499252 catch waf violations and handle gracefully
    if (isWafViolation) {
        const error = (
            <>
                <p className='fw-bold'>
                    An error has occurred. This form contains invalid characters.
                </p>
                <p className='mb-0'>
                    Please avoid special characters and where possible use only letters, numbers, comma and period separators before trying again.
                </p>
            </>
        );
        return renderServerError(error);
    }

    if (serverErrors) {
        let error;
        switch (serverErrors.status) {
            case HttpStatusCode.Conflict:
                error = (
                    <p className='fw-bold'>
                        Another person has already saved this page.
                        Your changes have not been saved.
                    </p>
                );
                break;
            case HttpStatusCode.UnprocessableEntity:
                error = (
                    <p className='fw-bold'>
                        Another person has already submitted this form. Please
                        <Link to='/dashboard'> go to the Dashboard </Link>
                        to view the submitted form.
                    </p>
                );
                break;
            case HttpStatusCode.Forbidden:
                error = (
                    <p className='fw-bold'>
                        We are unable to process your request. Your changes have not been saved. Please contact support.
                    </p>
                );
                break;
            default:
                error = (
                    <p className='fw-bold'>
                        Server error
                    </p>
                );
                break;
        }

        return renderServerError(error);
    }

    return (
        <FormikErrorsSummary
            disableLinkedError={disableLinkedError}
            suppressErrorSummaryPath={suppressErrorSummaryPath}
        />
    );
};

export default ErrorSummary;
